import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import ComposeDialog from "@/components/dashboard/ComposeDialog";
import { toast } from "sonner";
import {
  Inbox,
  Send,
  FileText,
  Star,
  Layers,
  Plus,
  Paperclip,
  RefreshCw,
  Reply,
  Forward,
  Trash2,
  ArrowLeft,
  Eye,
  EyeOff,
  Settings as SettingsIcon,
  Mail,
  AtSign,
  Search,
  X,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const FOLDERS = [
  { key: "inbox", label: "Inbox", icon: Inbox },
  { key: "sent", label: "Sent", icon: Send },
  { key: "drafts", label: "Drafts", icon: FileText },
  { key: "starred", label: "Starred", icon: Star },
];

const fromName = (from = "") => {
  const m = from.match(/^\s*"?([^"<]+?)"?\s*(<.*>)?$/);
  return (m && m[1].trim()) || from;
};

const EmailTab = ({ onOpenSettings }) => {
  const [configured, setConfigured] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [folder, setFolder] = useState("inbox");
  const [account, setAccount] = useState("all");
  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [thread, setThread] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState(false);
  const [compose, setCompose] = useState(null); // {mode, to, cc, subject, body, account, thread_id}
  const [imapOpen, setImapOpen] = useState(false);
  const [checked, setChecked] = useState(() => new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [activeQ, setActiveQ] = useState(""); // the search actually applied
  const [searchParams, setSearchParams] = useSearchParams();

  const runSearch = () => setActiveQ(searchInput.trim());
  const clearSearch = () => { setSearchInput(""); setActiveQ(""); };
  // changing folder/account exits search mode
  const pickFolder = (k) => { setFolder(k); clearSearch(); };
  const pickAccount = (a) => { setAccount(a); clearSearch(); };

  const keyOf = (m) => `${m.account}__${m.id}`;
  const toggleOne = (m) =>
    setChecked((prev) => {
      const next = new Set(prev);
      const k = keyOf(m);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });

  const loadAccounts = useCallback(async () => {
    try {
      const r = await api.get("/email/accounts");
      setConfigured(r.data.configured);
      setAccounts(r.data.accounts || []);
      return r.data;
    } catch {
      return { configured: false, accounts: [] };
    }
  }, []);

  const [nextTokens, setNextTokens] = useState({});
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadMessages = useCallback(async () => {
    setLoadingList(true);
    setSelected(null);
    setChecked(new Set());
    try {
      const r = await api.get(`/email/messages`, { params: { folder, account, q: activeQ || undefined, limit: 200 } });
      setMessages(r.data.messages || []);
      setNextTokens(r.data.next_tokens || {});
      setHasMore(!!r.data.has_more);
      setThread([]);
    } catch {
      setMessages([]);
      setHasMore(false);
    } finally {
      setLoadingList(false);
    }
  }, [folder, account, activeQ]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const r = await api.get(`/email/messages`, {
        params: { folder, account, q: activeQ || undefined, limit: 200, page_tokens: JSON.stringify(nextTokens) },
      });
      setMessages((prev) => {
        const seen = new Set(prev.map((m) => `${m.account}-${m.id}`));
        return [...prev, ...(r.data.messages || []).filter((m) => !seen.has(`${m.account}-${m.id}`))];
      });
      setNextTokens(r.data.next_tokens || {});
      setHasMore(!!r.data.has_more);
    } catch {
      toast.error("Could not load more messages");
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, folder, account, activeQ, nextTokens]);

  useEffect(() => {
    loadAccounts();
    // handle oauth return
    const params = new URLSearchParams(window.location.search);
    if (params.get("gmail") === "connected") {
      toast.success("Gmail account connected");
      window.history.replaceState(null, "", "/dashboard");
      loadAccounts();
    } else if (params.get("gmail") === "error") {
      const why = params.get("why");
      const msgs = {
        state: "The Google sign-in session expired before it finished — try connecting again.",
        token: "Google rejected the connection at the last step. Check Settings → API credentials and try again.",
        save: "Google connected but saving the mailbox failed — check the server logs.",
      };
      toast.error(msgs[why] || "Could not connect Gmail", { duration: 8000 });
      window.history.replaceState(null, "", "/dashboard");
    }
  }, [loadAccounts]);

  useEffect(() => {
    if (accounts.length > 0) loadMessages();
    else setMessages([]);
  }, [accounts.length, folder, account, loadMessages]);

  const connectGmail = () => {
    window.location.href = `${API}/oauth/gmail/login`;
  };

  const allChecked = messages.length > 0 && checked.size === messages.length;
  const toggleAll = () => {
    setChecked(allChecked ? new Set() : new Set(messages.map(keyOf)));
  };

  const doDelete = async () => {
    const items = messages.filter((m) => checked.has(keyOf(m))).map((m) => ({ account: m.account, message_id: m.id }));
    if (items.length === 0) return;
    setDeleting(true);
    try {
      await api.post("/email/delete", { messages: items });
      toast.success(`Deleted ${items.length} email${items.length > 1 ? "s" : ""}`);
      setConfirmDelete(false);
      await loadMessages();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete emails");
    } finally {
      setDeleting(false);
    }
  };

  const onMailboxAdded = async (email) => {
    setImapOpen(false);
    toast.success(`Mailbox ${email} connected`);
    await loadAccounts();
  };

  const openMessage = async (m) => {
    setLoadingMsg(true);
    setSelected({ id: m.id, loading: true });
    try {
      const r = await api.get(`/email/message/${encodeURIComponent(m.account)}/${m.id}`);
      setSelected(r.data);
      // Load the conversation thread (inbox + sent) so sent invoices sit
      // next to customer replies.
      api.get("/email/thread", { params: { subject: r.data.subject || "", account: m.account } })
        .then((t) => setThread(t.data.thread || []))
        .catch(() => setThread([]));
      if (m.unread) {
        api.post("/email/modify", { account: m.account, message_id: m.id, remove_labels: ["UNREAD"] }).catch(() => {});
        setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, unread: false } : x)));
      }
    } catch {
      toast.error("Could not open message");
      setSelected(null);
    } finally {
      setLoadingMsg(false);
    }
  };

  const startCompose = (mode = "new") => {
    const acc = account !== "all" ? account : accounts[0]?.email;
    if (!acc) return;
    if (mode === "reply" && selected) {
      setCompose({
        mode, account: selected.account, to: selected.from, cc: "",
        subject: selected.subject?.startsWith("Re:") ? selected.subject : `Re: ${selected.subject || ""}`,
        body: `\n\n---------- On ${selected.date}, ${fromName(selected.from)} wrote: ----------\n${selected.text || ""}`,
        thread_id: selected.thread_id, files: [],
      });
    } else if (mode === "forward" && selected) {
      setCompose({
        mode, account: selected.account, to: "", cc: "",
        subject: selected.subject?.startsWith("Fwd:") ? selected.subject : `Fwd: ${selected.subject || ""}`,
        body: `\n\n---------- Forwarded message ----------\nFrom: ${selected.from}\nDate: ${selected.date}\nSubject: ${selected.subject}\n\n${selected.text || ""}`,
        thread_id: null, files: [],
      });
    } else {
      setCompose({ mode: "new", account: acc, to: "", cc: "", subject: "", body: "", thread_id: null, files: [] });
    }
  };

  // Auto-open Compose when arriving from another tab with ?compose_to=<email>.
  useEffect(() => {
    const to = searchParams.get("compose_to");
    if (!to || accounts.length === 0 || compose) return;
    const acc = account !== "all" ? account : accounts[0]?.email;
    setCompose({
      mode: "new",
      account: acc,
      to,
      cc: "",
      subject: "",
      body: "",
      thread_id: null,
      files: [],
    });
    // Strip the params so a reload / re-mount doesn't re-open the dialog.
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete("compose_to");
        p.delete("compose_name");
        return p;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, accounts.length]);

  // Open a conversation when arriving with ?thread=<subject> (e.g. the Reply
  // badge on the Billing tab).
  useEffect(() => {
    const subj = searchParams.get("thread");
    if (!subj || accounts.length === 0) return;
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete("thread");
        return p;
      },
      { replace: true },
    );
    api
      .get("/email/thread", { params: { subject: subj, account: "all" } })
      .then((r) => {
        const t = r.data.thread || [];
        if (t.length) {
          openMessage(t[0]);
        } else {
          toast.error("No emails found for that invoice yet");
        }
      })
      .catch(() => toast.error("Could not load the conversation"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, accounts.length]);

  if (accounts.length === 0) {
    return (
      <>
        <ConnectScreen
          configured={configured}
          onConnectGmail={connectGmail}
          onAddMailbox={() => setImapOpen(true)}
          onOpenSettings={onOpenSettings}
        />
        {imapOpen && <ImapConnectDialog onClose={() => setImapOpen(false)} onAdded={onMailboxAdded} gmailConfigured={configured} />}
      </>
    );
  }

  return (
    <div className="flex h-[calc(100vh-96px)] flex-col bg-[#f6f8fc] text-gray-800" data-testid="email-tab">
      {/* compact toolbar — always visible (esp. when the sidebar is hidden on smaller screens) */}
      <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 bg-white px-3 py-2 md:hidden">
        <button onClick={() => startCompose("new")} data-testid="compose-btn-mobile" className="flex items-center gap-1.5 rounded-full bg-[#1a3fd1] px-3 py-1.5 text-xs font-semibold text-white">
          <Plus className="h-3.5 w-3.5" /> Compose
        </button>
        <select value={folder} onChange={(e) => pickFolder(e.target.value)} data-testid="folder-select-mobile" className="rounded-md border border-gray-300 px-2 py-1.5 text-xs text-gray-700">
          {FOLDERS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
        </select>
        <select value={account} onChange={(e) => pickAccount(e.target.value)} data-testid="account-select-mobile" className="max-w-[40%] truncate rounded-md border border-gray-300 px-2 py-1.5 text-xs text-gray-700">
          <option value="all">All inboxes</option>
          {accounts.map((a) => <option key={a.email} value={a.email}>{a.email}</option>)}
        </select>
        <button onClick={() => setImapOpen(true)} data-testid="add-mailbox-btn-mobile" className="ml-auto flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1.5 text-xs text-[#1a3fd1]">
          <AtSign className="h-3.5 w-3.5" /> Add
        </button>
      </div>

      <div className="flex min-h-0 flex-1">
      {/* sidebar */}
      <aside className="hidden w-60 shrink-0 flex-col border-r border-gray-200 bg-white p-3 md:flex">
        <button
          onClick={() => startCompose("new")}
          data-testid="compose-btn"
          className="mb-4 flex items-center justify-center gap-2 rounded-full bg-[#1a3fd1] px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-[#1a3fd1]/90"
        >
          <Plus className="h-4 w-4" /> Compose
        </button>

        <button
          onClick={() => pickAccount("all")}
          data-testid="folder-all-inboxes"
          className={`mb-1 flex items-center gap-3 rounded-r-full px-4 py-2 text-sm ${account === "all" ? "bg-[#e6ecfb] font-semibold text-[#1a3fd1]" : "text-gray-600 hover:bg-gray-100"}`}
        >
          <Layers className="h-4 w-4" /> All inboxes
        </button>

        {FOLDERS.map((f) => {
          const Icon = f.icon;
          const active = folder === f.key;
          return (
            <button
              key={f.key}
              onClick={() => pickFolder(f.key)}
              data-testid={`folder-${f.key}`}
              className={`mb-1 flex items-center gap-3 rounded-r-full px-4 py-2 text-sm ${active ? "bg-[#e6ecfb] font-semibold text-[#1a3fd1]" : "text-gray-600 hover:bg-gray-100"}`}
            >
              <Icon className="h-4 w-4" /> {f.label}
            </button>
          );
        })}

        <div className="mt-5 border-t border-gray-200 pt-3">
          <p className="mb-2 px-4 text-[11px] font-semibold uppercase tracking-wide text-gray-400">Accounts</p>
          <button
            onClick={() => pickAccount("all")}
            className={`block w-full rounded-md px-4 py-1.5 text-left text-xs ${account === "all" ? "font-semibold text-[#1a3fd1]" : "text-gray-600 hover:bg-gray-100"}`}
          >
            View all inboxes
          </button>
          {accounts.map((a) => (
            <button
              key={a.email}
              onClick={() => pickAccount(a.email)}
              data-testid="account-item"
              className={`block w-full truncate rounded-md px-4 py-1.5 text-left text-xs ${account === a.email ? "font-semibold text-[#1a3fd1]" : "text-gray-600 hover:bg-gray-100"}`}
            >
              {a.email}
            </button>
          ))}
          {configured && (
            <button
              onClick={connectGmail}
              data-testid="connect-account-btn"
              className="mt-1 flex w-full items-center gap-2 rounded-md px-4 py-1.5 text-left text-xs text-[#1a3fd1] hover:bg-gray-100"
            >
              <Plus className="h-3.5 w-3.5" /> Connect Gmail
            </button>
          )}
          <button
            onClick={() => setImapOpen(true)}
            data-testid="add-mailbox-btn"
            className="mt-0.5 flex w-full items-center gap-2 rounded-md px-4 py-1.5 text-left text-xs text-[#1a3fd1] hover:bg-gray-100"
          >
            <AtSign className="h-3.5 w-3.5" /> Add mailbox (Gmail / IMAP)
          </button>
        </div>
      </aside>

      {/* message list */}
      <div className={`w-full flex-col border-r border-gray-200 bg-white md:w-96 ${selected ? "hidden lg:flex" : "flex"}`}>
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <span className="text-sm font-semibold capitalize text-gray-700">
            {activeQ ? <span data-testid="email-search-context">Results for “{activeQ}”</span> : <>{account === "all" ? "All inboxes" : account} · {folder}</>}
          </span>
          <button onClick={loadMessages} aria-label="Refresh" className="rounded p-1.5 text-gray-500 hover:bg-gray-100">
            <RefreshCw className={`h-4 w-4 ${loadingList ? "animate-spin" : ""}`} />
          </button>
        </div>
        {/* search — server-side across subject, sender and body (Gmail q / IMAP SEARCH) */}
        <div className="flex items-center gap-1.5 border-b border-gray-200 bg-white px-3 py-2">
          <Search className="h-3.5 w-3.5 shrink-0 text-gray-400" />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") runSearch(); if (e.key === "Escape") clearSearch(); }}
            placeholder="Search mail — Enter"
            data-testid="email-search-input"
            className="min-w-0 flex-1 bg-transparent text-xs text-gray-800 placeholder-gray-400 focus:outline-none"
          />
          {activeQ && (
            <button onClick={clearSearch} data-testid="email-search-clear" aria-label="Clear search"
              className="rounded p-0.5 text-gray-400 hover:text-gray-700">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {messages.length > 0 && (
          <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-4 py-2">
            <label className="flex cursor-pointer items-center gap-2 text-xs font-medium text-gray-600">
              <Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="email-select-all" />
              {checked.size > 0 ? `${checked.size} selected` : "Select all"}
            </label>
            {checked.size > 0 && (
              <button
                onClick={() => setConfirmDelete(true)}
                data-testid="email-delete-selected"
                className="flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700"
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete ({checked.size})
              </button>
            )}
          </div>
        )}
        <div className="flex-1 overflow-y-auto">
          {loadingList ? (
            <div className="py-16 text-center text-sm text-gray-400">Loading…</div>
          ) : messages.length === 0 ? (
            <div className="py-16 text-center text-sm text-gray-400" data-testid="email-empty">
              {activeQ ? `No results for “${activeQ}”` : "No messages"}
            </div>
          ) : (
            <>
              {messages.map((m) => (
              <div
                key={`${m.account}-${m.id}`}
                data-testid="email-list-row"
                className={`flex items-start gap-2 border-b border-gray-100 px-3 py-3 hover:bg-[#f0f5ff] ${selected?.id === m.id ? "bg-[#eef3fe]" : "bg-white"}`}
              >
                <Checkbox
                  checked={checked.has(keyOf(m))}
                  onCheckedChange={() => toggleOne(m)}
                  data-testid="email-item-checkbox"
                  className="mt-1 shrink-0"
                />
                <button
                  onClick={() => openMessage(m)}
                  data-testid="email-list-item"
                  className="flex min-w-0 flex-1 flex-col gap-0.5 text-left"
                >
                  <div className="flex items-center justify-between">
                    <span className={`truncate text-sm ${m.unread ? "font-bold text-gray-900" : "font-medium text-gray-700"}`}>
                      {fromName(m.from)}
                    </span>
                    <span className="ml-2 shrink-0 text-[11px] text-gray-400">{(m.date || "").split(" ").slice(0, 4).join(" ")}</span>
                  </div>
                  <span className={`truncate text-xs ${m.unread ? "font-semibold text-gray-800" : "text-gray-600"}`}>
                    {m.subject || "(no subject)"}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="truncate text-xs text-gray-400">{m.snippet}</span>
                    {m.has_attachments && <Paperclip className="h-3 w-3 shrink-0 text-gray-400" />}
                    {m.starred && <Star className="h-3 w-3 shrink-0 fill-amber-400 text-amber-400" />}
                  </div>
                </button>
              </div>
              ))}
              {hasMore && (
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  data-testid="email-load-more"
                  className="flex w-full items-center justify-center gap-2 py-3 text-xs font-semibold text-[#1a4fa0] hover:bg-[#f0f5ff] disabled:opacity-50"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${loadingMore ? "animate-spin" : ""}`} />
                  {loadingMore ? "Loading…" : `Load more (${messages.length} shown)`}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* reading pane */}
      <div className={`flex-1 flex-col bg-white ${selected ? "flex" : "hidden lg:flex"}`}>
        {!selected ? (
          <div className="flex flex-1 items-center justify-center text-sm text-gray-400">Select a message to read</div>
        ) : loadingMsg ? (
          <div className="flex flex-1 items-center justify-center text-sm text-gray-400">Loading message…</div>
        ) : (
          <div className="flex flex-1 flex-col overflow-hidden" data-testid="email-reading-pane">
            <div className="border-b border-gray-200 px-6 py-4">
              <button
                onClick={() => { setSelected(null); setThread([]); }}
                data-testid="email-back-btn"
                className="mb-3 flex items-center gap-1 text-xs font-medium text-[#1a3fd1] hover:underline lg:hidden"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Back to list
              </button>
              <h2 className="text-lg font-semibold text-gray-900">{selected.subject || "(no subject)"}</h2>
              <div className="mt-2 flex items-center justify-between">
                <div className="text-sm">
                  <p className="font-medium text-gray-800">{selected.from}</p>
                  <p className="text-xs text-gray-500">to {selected.to}</p>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => startCompose("reply")} data-testid="reply-btn" className="flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
                    <Reply className="h-3.5 w-3.5" /> Reply
                  </button>
                  <button onClick={() => startCompose("forward")} data-testid="forward-btn" className="flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
                    <Forward className="h-3.5 w-3.5" /> Forward
                  </button>
                </div>
              </div>
            </div>
            {/* conversation thread — sent invoices appear beside replies */}
            {thread.length > 1 && (
              <div className="border-b border-gray-100 px-6 py-2" data-testid="email-thread">
                <p className="mb-1.5 font-mono text-[9px] uppercase tracking-widest text-gray-400">Thread · {thread.length} messages</p>
                <div className="flex flex-wrap gap-1.5">
                  {thread.map((t) => {
                    const isCurrent = t.id === selected.id;
                    const isSent = t.folder === "sent" || String(t.id).toLowerCase().startsWith("sent");
                    return (
                      <button
                        key={t.id}
                        onClick={() => !isCurrent && openMessage(t)}
                        data-testid={`thread-msg-${t.id}`}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
                          isCurrent
                            ? "border-[#1a3fd1] bg-[#1a3fd1]/10 text-[#1a3fd1]"
                            : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                        }`}
                      >
                        <span className={`rounded px-1 py-0.5 font-mono text-[8px] font-bold uppercase ${isSent ? "bg-[#1a3fd1]/15 text-[#1a3fd1]" : "bg-gray-200 text-gray-500"}`}>
                          {isSent ? "Sent" : "Inbox"}
                        </span>
                        <span className="max-w-[180px] truncate">{fromName(t.from) || t.from}</span>
                        <span className="text-gray-400">{t.date}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {selected.html ? (
                <iframe title="email-body" srcDoc={selected.html} className="h-full min-h-[300px] w-full border-0" sandbox="" />
              ) : (
                <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800">{selected.text}</pre>
              )}
              {selected.attachments?.length > 0 && (
                <div className="mt-4 border-t border-gray-200 pt-4">
                  <p className="mb-2 text-xs font-semibold uppercase text-gray-500">
                    {selected.attachments.length} attachment(s)
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {selected.attachments.map((a) => (
                      <a
                        key={a.attachment_id}
                        href={`${API}/email/attachment/${encodeURIComponent(selected.account)}/${selected.id}/${a.attachment_id}?filename=${encodeURIComponent(a.filename)}`}
                        data-testid="attachment-link"
                        className="flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                      >
                        <Paperclip className="h-3.5 w-3.5" /> {a.filename}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      </div>

      {compose && (
        <ComposeDialog
          data={compose}
          accounts={accounts}
          onClose={() => setCompose(null)}
          onSent={() => {
            setCompose(null);
            toast.success("Email sent");
            if (folder === "sent") loadMessages();
          }}
        />
      )}
      {imapOpen && <ImapConnectDialog onClose={() => setImapOpen(false)} onAdded={onMailboxAdded} gmailConfigured={configured} />}

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {checked.size} email{checked.size > 1 ? "s" : ""}?</AlertDialogTitle>
            <AlertDialogDescription>
              The selected {checked.size > 1 ? "messages" : "message"} will be moved to Trash.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">No</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); doDelete(); }}
              disabled={deleting}
              data-testid="delete-confirm"
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {deleting ? "Deleting…" : "Yes, delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const ConnectScreen = ({ configured, onConnectGmail, onAddMailbox, onOpenSettings }) => (
  <div className="flex min-h-[60vh] items-center justify-center px-6" data-testid="email-connect">
    <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center">
      <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#1a3fd1]/15 text-[#8fa4f0]">
        <Mail className="h-6 w-6" />
      </div>
      <h2 className="font-display text-xl font-bold uppercase tracking-tight text-white">Connect your email</h2>
      <p className="mt-3 text-sm text-white/50">
        Add a mailbox to bring your inbox, sent mail and more into the portal.
      </p>

      <div className="mt-6 space-y-3">
        <button
          onClick={onAddMailbox}
          data-testid="add-mailbox-btn"
          className="flex w-full items-center justify-center gap-2 rounded-md bg-[#1a3fd1] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#1a3fd1]/85"
        >
          <AtSign className="h-4 w-4" /> Add mailbox (Gmail · Outlook · IMAP)
        </button>

        {configured ? (
          <button
            onClick={onConnectGmail}
            data-testid="connect-account-btn"
            className="flex w-full items-center justify-center gap-2 rounded-md border border-white/15 px-5 py-2.5 text-sm font-semibold text-white hover:border-[#1a3fd1]"
          >
            <Mail className="h-4 w-4" /> Connect Gmail
          </button>
        ) : (
          <button
            onClick={onOpenSettings}
            data-testid="email-configure-gmail"
            className="flex w-full items-center justify-center gap-2 rounded-md border border-white/10 px-5 py-2.5 text-xs text-white/50 hover:border-white/25 hover:text-white/80"
          >
            <SettingsIcon className="h-3.5 w-3.5" /> Add Google API keys to enable Gmail
          </button>
        )}
      </div>
    </div>
  </div>
);

const IMAP_PRESETS = {
  namecheap: { label: "Namecheap Private Email", imap_host: "mail.privateemail.com", imap_port: 993, smtp_host: "mail.privateemail.com", smtp_port: 465 },
  gmail: { label: "Gmail", imap_host: "imap.gmail.com", imap_port: 993, smtp_host: "smtp.gmail.com", smtp_port: 465, isGmail: true },
};

const ImapConnectDialog = ({ onClose, onAdded, gmailConfigured }) => {
  const [preset, setPreset] = useState("namecheap");
  const [form, setForm] = useState({
    email: "", password: "",
    imap_host: IMAP_PRESETS.namecheap.imap_host, imap_port: 993,
    smtp_host: IMAP_PRESETS.namecheap.smtp_host, smtp_port: 465,
  });
  const [advanced, setAdvanced] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const applyPreset = (key) => {
    const p = IMAP_PRESETS[key];
    setPreset(key);
    setForm((s) => ({ ...s, imap_host: p.imap_host, imap_port: p.imap_port, smtp_host: p.smtp_host, smtp_port: p.smtp_port }));
  };
  const isGmail = IMAP_PRESETS[preset]?.isGmail;
  const startGoogleOAuth = () => {
    window.location.href = `${API}/oauth/gmail/login`;
  };

  const submit = async () => {
    if (!form.email.trim() || !form.password) {
      toast.error("Enter your email address and password");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/email/imap/connect", {
        email: form.email.trim(),
        password: form.password,
        imap_host: form.imap_host.trim(),
        imap_port: Number(form.imap_port) || 993,
        smtp_host: form.smtp_host.trim(),
        smtp_port: Number(form.smtp_port) || 465,
      });
      onAdded(r.data.email);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not connect mailbox");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white text-gray-800 sm:max-w-lg" data-testid="imap-connect-dialog">
        <DialogHeader>
          <DialogTitle>Add a mailbox</DialogTitle>
          <DialogDescription className="text-gray-500">
            Connect a mailbox from your Namecheap domain (or any IMAP/SMTP provider). We use IMAP to read and SMTP to send.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {Object.entries(IMAP_PRESETS).map(([key, p]) => (
              <button
                key={key}
                onClick={() => applyPreset(key)}
                data-testid={`imap-preset-${key}`}
                className={`rounded-md border px-3 py-1.5 text-xs font-medium ${
                  preset === key
                    ? "border-[#1a3fd1] bg-[#eef3fe] text-[#1a3fd1]"
                    : "border-gray-300 text-gray-600 hover:bg-gray-50"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {IMAP_PRESETS[preset]?.hint && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] leading-snug text-amber-800" data-testid="imap-preset-hint">
              {IMAP_PRESETS[preset].hint}
            </div>
          )}

          {isGmail ? (
            <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4 text-center">
              <p className="text-xs text-gray-600">
                Sign in with your Google account to grant this portal access to Gmail. Nothing is stored except a refresh token that lets us sync your inbox.
              </p>
              {gmailConfigured ? (
                <button
                  onClick={startGoogleOAuth}
                  data-testid="imap-connect-google-btn"
                  className="mx-auto flex items-center gap-3 rounded-md border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
                >
                  <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden="true">
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                  </svg>
                  Continue with Google
                </button>
              ) : (
                <p className="text-[11px] text-red-600">
                  Google OAuth isn&apos;t configured yet. Ask a superadmin to paste the Client ID + Secret under Admin Settings → API.
                </p>
              )}
            </div>
          ) : (
          <>
          <div>
            <label className="text-xs font-medium text-gray-500">Email address</label>
            <Input
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              placeholder="you@yourdomain.com"
              data-testid="imap-email-input"
              className="mt-1 border-gray-300 text-gray-800"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Password</label>
            <div className="relative mt-1">
              <Input
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={(e) => set("password", e.target.value)}
                placeholder="Mailbox password"
                data-testid="imap-password-input"
                className="border-gray-300 pr-10 text-gray-800"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                data-testid="imap-password-toggle"
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:text-gray-700"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="mt-1 text-[11px] text-gray-400">Stored securely on your server and used only to sync this mailbox.</p>
          </div>

          <button
            onClick={() => setAdvanced((v) => !v)}
            data-testid="imap-advanced-toggle"
            className="text-xs font-medium text-[#1a3fd1] hover:underline"
          >
            {advanced ? "Hide" : "Show"} server settings
          </button>

          {advanced && (
            <div className="grid grid-cols-2 gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div>
                <label className="text-[11px] font-medium text-gray-500">IMAP host</label>
                <Input value={form.imap_host} onChange={(e) => set("imap_host", e.target.value)} data-testid="imap-host-input" className="mt-1 border-gray-300 text-gray-800" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-gray-500">IMAP port</label>
                <Input value={form.imap_port} onChange={(e) => set("imap_port", e.target.value)} data-testid="imap-port-input" className="mt-1 border-gray-300 text-gray-800" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-gray-500">SMTP host</label>
                <Input value={form.smtp_host} onChange={(e) => set("smtp_host", e.target.value)} data-testid="smtp-host-input" className="mt-1 border-gray-300 text-gray-800" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-gray-500">SMTP port</label>
                <Input value={form.smtp_port} onChange={(e) => set("smtp_port", e.target.value)} data-testid="smtp-port-input" className="mt-1 border-gray-300 text-gray-800" />
              </div>
            </div>
          )}
          </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-gray-300 text-gray-700">Cancel</Button>
          {!isGmail && (
            <Button onClick={submit} disabled={saving} data-testid="imap-connect-submit" className="bg-[#1a3fd1] text-white hover:bg-[#1a3fd1]/85">
              {saving ? "Connecting…" : "Connect mailbox"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmailTab;
